import type { OtaManifest } from "./manifest";

export type UpdateDecision =
  | "download"
  | "skip-failed"
  | "skip-native-too-old"
  | "skip-pending"
  | "skip-same";

export interface DecideUpdateInput {
  /** The bundle this JavaScript was built as: `VITE_BUNDLE_VERSION`. */
  readonly currentVersion: string;
  /**
   * A version that was queued earlier and never became the running bundle:
   * the plugin rolled it back. Downloading it again would roll back again on
   * every other launch, each time after a 10 s wait.
   */
  readonly failedVersion?: string | null;
  readonly manifest: OtaManifest;
  /** The installed APK's `versionCode`. */
  readonly nativeBuild: number;
  /** The version already downloaded and waiting for the next launch. */
  readonly pendingVersion?: string | null;
}

/**
 * Whether the manifest's bundle should be downloaded. Pure.
 *
 * Download only when the manifest names another bundle than the one running
 * AND the installed APK is new enough for it (`minNativeBuild`). A bundle
 * built against newer native code would call plugins this APK does not have.
 */
export function decideUpdate(input: DecideUpdateInput): UpdateDecision {
  const { manifest } = input;
  if (manifest.version === input.currentVersion) {
    return "skip-same";
  }
  if (
    !Number.isInteger(input.nativeBuild) ||
    manifest.minNativeBuild > input.nativeBuild
  ) {
    return "skip-native-too-old";
  }
  if (manifest.version === input.pendingVersion) {
    return "skip-pending";
  }
  if (manifest.version === input.failedVersion) {
    return "skip-failed";
  }
  return "download";
}

/**
 * The version a previous launch queued, if the plugin has since dropped it
 * without it ever running. `queued` is what this app wrote before `next()`;
 * `pending` is what the plugin still holds as next.
 */
export function rolledBackVersion(input: {
  readonly currentVersion: string;
  readonly pendingVersion: string | null;
  readonly queued: string | null;
}): string | null {
  const { queued } = input;
  if (
    queued === null ||
    queued === input.currentVersion ||
    queued === input.pendingVersion
  ) {
    return null;
  }
  return queued;
}

export interface PendingBundle {
  readonly id: string;
  readonly status: string;
}

/**
 * Whether a bundle queued by an earlier launch should be switched to now, at
 * boot, before anything renders.
 *
 * The installed plugin (8.51.19, Android) applies a `next()` bundle ONLY when
 * the app goes to the background (`installNext` runs from `onActivityStopped`
 * alone). The `kill` condition is merely cleared at the next launch, so on its
 * own it moves the switch to the first background AFTER a relaunch — the
 * music-app-between-sets case D07 forbids. Applying it here, at boot, is what
 * makes "the next time the app opens" true.
 *
 * `undefined`, not only `null`. The plugin's own types say
 * `getNextBundle(): Promise<BundleInfo | null>`, and on Android with nothing
 * queued the bridge resolves with NO value at all — `undefined`, which is
 * not `null`. Reading `.id` off it threw on EVERY launch of a bundled build,
 * and `applyQueuedBundle`'s `catch` turned that into one `[ota] apply failed`
 * line and a `false` — so a bundle that WAS queued would never have been
 * applied at boot either. Observed on the container on 2026-09-21.
 */
export function shouldApplyPending(input: {
  readonly currentId: string;
  readonly pending: PendingBundle | null | undefined;
}): boolean {
  const { pending } = input;
  return (
    pending !== null &&
    pending !== undefined &&
    pending.id !== input.currentId &&
    pending.status !== "error"
  );
}

/** What one check found: a bundle ready, or why there is none. */
export type CheckOutcome =
  | { readonly kind: "queued" | "pending"; readonly version: string }
  | { readonly kind: "same" | "native-too-old" | "failed" | "unreadable" };

/** A check that downloaded nothing, read from its decision. Pure. */
export function outcomeOfDecision(
  decision: Exclude<UpdateDecision, "download">,
  version: string
): CheckOutcome {
  switch (decision) {
    case "skip-pending":
      // Downloaded by an earlier check and still queued: ready all the same.
      return { kind: "pending", version };
    case "skip-native-too-old":
      return { kind: "native-too-old" };
    case "skip-failed":
      return { kind: "failed" };
    default:
      return { kind: "same" };
  }
}

/** What "Procurar atualizações" tells the person (`D187`). */
export type ManualUpdateCheck =
  /** Web, or a live-reload build: there is nothing this app can install. */
  | { readonly kind: "unsupported" }
  | { readonly kind: "current" }
  /** Downloaded and queued: restarting now opens it. */
  | { readonly kind: "ready"; readonly version: string }
  /** The new bundle needs a newer app from the store (`minNativeBuild`). */
  | { readonly kind: "store" }
  /** Offline, or the manifest could not be read. */
  | { readonly kind: "failed" };

/** One check's outcome, as the person reads it. Pure. */
export function manualResultOf(outcome: CheckOutcome): ManualUpdateCheck {
  switch (outcome.kind) {
    case "queued":
    case "pending":
      return { kind: "ready", version: outcome.version };
    case "native-too-old":
      return { kind: "store" };
    case "unreadable":
      return { kind: "failed" };
    default:
      // `same`, and `failed`: a version that already rolled back once is not
      // offered again, so for the person there is nothing newer to install.
      return { kind: "current" };
  }
}
